
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "./use-toast";

// Función para validar UUIDs
const isUUID = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

export const useCustomRoutineName = (routineId: string) => {
  const queryClient = useQueryClient();

  // Consultar directamente desde la tabla routines, no desde custom_routine_names
  const { data: customName, isLoading } = useQuery({
    queryKey: ["routine-name", routineId],
    queryFn: async () => {
      if (!routineId || !isUUID(routineId)) {
        console.warn("⛔️ Invalid routineId:", routineId);
        return null;
      }

      const { data, error } = await supabase
        .from("routines")
        .select("name")
        .eq("id", routineId)
        .maybeSingle();

      if (error) {
        console.error("🔥 Error fetching routine name:", error);
        throw error;
      }

      return data?.name ?? null;
    },
    enabled: !!routineId && isUUID(routineId),
    staleTime: 30000,
  });

  const updateRoutineName = async (newName: string) => {
    if (!newName.trim()) {
      throw new Error("El nombre no puede estar vacío");
    }

    console.log(`🔄 Starting routine name update: ${routineId} -> "${newName}"`);
    
    try {
      // Optimistic update - Update cache immediately for instant UI feedback
      queryClient.setQueryData(["routine-name", routineId], newName);
      console.log(`⚡ Optimistic update applied for routine ${routineId}`);

      // Update database
      const { error } = await supabase
        .from("routines")
        .update({ name: newName.trim() })
        .eq("id", routineId);

      if (error) {
        console.error("❌ Database update failed:", error);
        // Revert optimistic update on error
        queryClient.setQueryData(["routine-name", routineId], customName);
        throw error;
      }

      console.log(`✅ Database updated successfully for routine ${routineId}`);

      // Clean up any legacy custom names
      try {
        await supabase
          .from("custom_routine_names")
          .delete()
          .eq("routine_id", routineId);
        console.log(`🧹 Legacy custom names cleaned up for routine ${routineId}`);
      } catch (cleanupError) {
        console.warn("⚠️ Cleanup of legacy names failed (non-critical):", cleanupError);
      }

      // Selective cache invalidation - only invalidate what's necessary
      const criticalQueries = [
        ["routines"],
        ["routine-name", routineId],
        ["workout-plan-routines"],
        ["workout-plans"]
      ];

      console.log(`🔄 Invalidating ${criticalQueries.length} critical query types`);
      
      for (const queryKey of criticalQueries) {
        queryClient.invalidateQueries({ queryKey });
      }

      console.log(`✅ Routine name update completed successfully`);

    } catch (error) {
      console.error("❌ Error in updateRoutineName:", error);
      // Ensure optimistic update is reverted on any error
      queryClient.setQueryData(["routine-name", routineId], customName);
      throw error;
    }
  };

  return { 
    customName, 
    isLoading, 
    updateRoutineName 
  };
};

export const useCustomExerciseName = (exerciseId: string) => {
  const queryClient = useQueryClient();

  const { data: customName } = useQuery({
    queryKey: ["custom-exercise-name", exerciseId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const isValid = user?.id && isUUID(exerciseId);
      
      if (!isValid) {
        console.warn("⛔️ Invalid query params:", { exerciseId, userId: user?.id });
        return null;
      }

      const { data, error } = await supabase
        .from("custom_exercise_names")
        .select("custom_name")
        .eq("exercise_id", exerciseId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        console.error("🔥 Supabase error:", error);
        return null;
      }

      return data?.custom_name ?? null;
    },
    enabled: !!exerciseId && isUUID(exerciseId),
    staleTime: 60000,
    retry: false,
  });

  const updateExerciseName = async (newName: string) => {
    try {
      if (!isUUID(exerciseId)) {
        console.warn("❌ Cannot update: invalid UUID for exerciseId", exerciseId);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { error } = await supabase
        .from("custom_exercise_names")
        .upsert({
          exercise_id: exerciseId,
          user_id: user.id,
          custom_name: newName,
        });

      if (error) throw error;

      console.log("✅ Exercise name updated successfully for exerciseId:", exerciseId);

      // Invalidar consultas relacionadas con ejercicios
      await queryClient.invalidateQueries({ queryKey: ["custom-exercise-name"] });
      await queryClient.invalidateQueries({ queryKey: ["workout-plan-routines"] });
      await queryClient.refetchQueries({ queryKey: ["custom-exercise-name", exerciseId] });

    } catch (error) {
      console.error("Error updating exercise name:", error);
      throw error;
    }
  };

  return { 
    customName, 
    updateExerciseName
  };
};
